/**
 * Frota · Manutenção — a Central de manutenção.
 *
 * Porta de entrada do módulo, como o cliente pediu (referência em docs/os):
 * localizar o veículo e escolher o tipo de registro ou consulta. Cada cartão leva a
 * uma tela que já existe; o que ainda não existe aparece como "Em breve" em vez de
 * um link morto. As ilustrações são recortes da referência, em /manutencao-central.
 *
 * O painel de custo do ERP que ocupava esta rota agora é "Custos de manutenção".
 */
import { BackButton } from "../components/BackButton";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PlateSearch } from "../components/PlateSearch";
import { IconChevronRight, IconPlus, IconTruck, IconUser, IconDocument, IconBarChart } from "../components/icons";
import { useMobile } from "../features/maintenance-mobile/mobile-ui";

interface Cartao {
  titulo: string;
  texto: string;
  imagem: string;
  /** Rota de destino; sem ela o cartão é "Em breve". */
  to?: string;
}

const REGISTRAR: Cartao[] = [
  { titulo: "Eixos e rodas", texto: "Rolamentos, cubos, graxa e retentores.", imagem: "eixos-rodas", to: "/frota/manutencao/nova?tipo=axles" },
  { titulo: "Óleos e filtros", texto: "Motor, diferencial, câmbio e ARLA.", imagem: "oleos-filtros", to: "/frota/manutencao/nova?tipo=oils" },
  { titulo: "Demais serviços", texto: "Freios, suspensão, elétrica e arrefecimento.", imagem: "demais-servicos", to: "/frota/manutencao/nova?tipo=others" },
];

const PLANEJAR: Cartao[] = [
  { titulo: "Manutenção preventiva", texto: "Próximas revisões por km ou data.", imagem: "preventiva", to: "/frota/manutencao/preventiva" },
  { titulo: "Checklist do veículo", texto: "Inspeção rápida antes da viagem.", imagem: "checklist", to: "/frota/manutencao/checklists" },
  { titulo: "Ordens de serviço", texto: "Abrir e acompanhar os serviços.", imagem: "ordens", to: "/frota/manutencao/ordens" },
];

const ACOMPANHAR: Cartao[] = [
  { titulo: "Histórico de manutenção", texto: "Consultar serviços realizados por veículo.", imagem: "historico", to: "/frota/manutencao/ordens?status=completed" },
  { titulo: "Peças e estoque", texto: "Controlar peças, materiais e reposições.", imagem: "pecas-estoque" },
  { titulo: "Custos de manutenção", texto: "Acompanhar gastos por veículo e período.", imagem: "custos", to: "/frota/manutencao/custos" },
];

function CartaoBloco({ c, placa }: { c: Cartao; placa: string }) {
  const navigate = useNavigate();
  const mobile = useMobile();
  const emBreve = !c.to;
  const destino = c.to && placa && c.to.includes("/nova?") ? `${c.to}&placa=${placa}` : c.to;
  // Celular: linha compacta (ilustração pequena, cartão inteiro tocável) — o botão "Abrir"
  // de 150 px ao lado de uma imagem de 150 px não cabe numa tela de 390.
  if (mobile) {
    const conteudo = (
      <>
        <img src={`/manutencao-central/${c.imagem}.png`} alt="" className="h-[3.875rem] w-[4.25rem] flex-shrink-0 object-contain mix-blend-multiply" loading="lazy" />
        <span className="min-w-0 flex-1">
          <span className={`block text-[0.9375rem] font-semibold leading-tight ${emBreve ? "text-gray-400" : "text-gray-900"}`}>{c.titulo}</span>
          <span className="mt-0.5 block text-xs leading-snug text-gray-500">{c.texto}</span>
        </span>
        {emBreve ? <span className="badge-gray">Em breve</span> : <IconChevronRight className="h-5 w-5 flex-shrink-0 text-tm-600" />}
      </>
    );
    return emBreve
      ? <div className="card flex items-center gap-3 px-3 py-2.5">{conteudo}</div>
      : <button type="button" onClick={() => destino && navigate(destino)} className="card flex w-full items-center gap-3 px-3 py-2.5 text-left active:bg-surface-muted">{conteudo}</button>;
  }
  return (
    <div className="card flex min-h-[9.875rem] items-center gap-5 px-5 py-4">
      <img src={`/manutencao-central/${c.imagem}.png`} alt="" className="h-[8.625rem] w-[9.375rem] flex-shrink-0 object-contain mix-blend-multiply" loading="lazy" />
      <div className="flex min-w-0 flex-1 flex-col self-stretch">
        <h4 className="text-xl font-semibold leading-tight text-gray-900">{c.titulo}</h4>
        <p className="mt-1.5 text-[0.9375rem] leading-snug text-gray-500">{c.texto}</p>
        <div className="mt-auto flex justify-end pt-3">
          <button
            type="button"
            disabled={emBreve}
            onClick={() => destino && navigate(destino)}
            title={emBreve ? "Em breve" : undefined}
            className="inline-flex items-center gap-2 rounded-lg border border-tm-600 bg-white px-5 py-2 text-[0.9375rem] font-medium text-tm-700 transition-colors hover:bg-tm-50 disabled:cursor-not-allowed disabled:border-surface-border disabled:text-gray-400 disabled:hover:bg-white"
          >
            {emBreve ? "Em breve" : "Abrir"}
            {!emBreve && <span aria-hidden="true">→</span>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Grupo({ titulo, cartoes, placa }: { titulo: string; cartoes: Cartao[]; placa: string }) {
  const mobile = useMobile();
  return (
    <section className={mobile ? "space-y-2" : "space-y-3"}>
      <h3 className={mobile ? "pt-1 text-[0.9375rem] font-bold text-gray-900" : "text-2xl font-bold tracking-tight text-gray-900"}>{titulo}</h3>
      <div className={`grid grid-cols-1 lg:grid-cols-3 ${mobile ? "gap-2.5" : "gap-4"}`}>
        {cartoes.map((c) => <CartaoBloco key={c.titulo} c={c} placa={placa} />)}
      </div>
    </section>
  );
}

export function MaintenanceHubPage() {
  const navigate = useNavigate();
  const mobile = useMobile();
  const placa = (useSearchParams()[0].get("placa") ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  return (
    <div className={mobile ? "space-y-3" : "space-y-6"}>
      <BackButton fallback="/frota/manutencao" />
      {mobile ? (
        <Link to="/frota/manutencao/ordens?nova=1" className="btn-primary min-h-[3rem] w-full justify-center">
          <IconPlus className="h-5 w-5" /> Nova ordem de serviço
        </Link>
      ) : (
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-4xl font-bold tracking-tight text-gray-900">Registrar manutenção</h2>
          <p className="mt-2 text-lg text-gray-500">Escolha o tipo de registro ou consulta.</p>
        </div>
        <Link to="/frota/manutencao/ordens?nova=1" className="btn-primary flex-shrink-0 px-6 py-3.5 text-lg">
          <IconPlus className="h-5 w-5" /> Nova ordem de serviço
        </Link>
      </header>
      )}

      <div className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3 sm:w-56">
          <IconTruck className="h-9 w-9 text-gray-700" />
          <span className="text-lg font-semibold text-gray-900">Localizar veículo</span>
        </div>
        <PlateSearch variant="hub" />
        <button
          type="button"
          disabled
          title="O cadastro de veículos vem do ERP (Sinersoft)"
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-tm-600 bg-white px-4 py-2 text-sm font-medium text-tm-700 disabled:cursor-not-allowed disabled:border-surface-border disabled:text-gray-400"
        >
          <IconTruck className="h-5 w-5" /> Cadastrar veículo
        </button>
      </div>

      <Grupo titulo="Registrar manutenção" cartoes={REGISTRAR} placa={placa} />
      <Grupo titulo="Planejar e executar" cartoes={PLANEJAR} placa={placa} />
      <Grupo titulo="Acompanhar e administrar" cartoes={ACOMPANHAR} placa={placa} />

      <div className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <span className="text-xl font-semibold text-gray-900 sm:border-r sm:border-surface-border sm:pr-6">Mais opções</span>
        {[
          { label: "Mecânicos e oficinas", Icon: IconUser },
          { label: "Documentos e anexos", Icon: IconDocument },
          { label: "Central de Manutenção", Icon: IconBarChart, to: "/frota/manutencao" },
        ].map(({ label, Icon, to }) => (
          <button
            key={label}
            type="button"
            disabled={!to}
            title={to ? undefined : "Em breve"}
            onClick={() => to && navigate(to)}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-tm-600 bg-white px-4 py-2 text-sm font-medium text-tm-700 hover:bg-tm-50 disabled:cursor-not-allowed disabled:border-surface-border disabled:text-gray-400 disabled:hover:bg-white"
          >
            <Icon className="h-5 w-5" /> {label}
          </button>
        ))}
      </div>
    </div>
  );
}
